/**
 * Asks for the next page while the end of the wall is still about a row of tiles below the fold, so
 * the trainer scrolling at reading speed meets tiles rather than the wait.
 *
 * The observer is rebuilt only when `armed` flips, and a new observer reports its target's state at
 * once — so a page that lands with the end still in reach asks for the one after it, where one
 * observer kept across the landing would never fire again. `load` is read through a ref, so a new
 * closure on every render does not rebuild it. The ref is a PARAMETER: the component owns it
 * (React Compiler's `react-hooks/refs`).
 *
 * The root is the nearest ancestor that scrolls, found at arming: a `rootMargin` extends only the
 * root, and every scroller between the target and a viewport root would clip the margin away. On
 * `/nagrania` that is the wall card; on the Forma gallery it is the client card's tab panel.
 */

import { useEffect, useRef, type RefObject } from 'react';

/** About one row of tiles: a 150px-wide tile's poster at 9:14 is 233px, and its three lines follow. */
const AHEAD = '0px 0px 360px 0px';

function scrollingAncestor(element: Element): Element | null {
  for (let parent = element.parentElement; parent !== null; parent = parent.parentElement) {
    if (/(auto|scroll)/.test(getComputedStyle(parent).overflowY)) return parent;
  }
  return null;
}

export function useLoadNearEnd(
  end: RefObject<Element | null>,
  armed: boolean,
  load: () => void,
): void {
  const latestLoad = useRef(load);
  useEffect(() => {
    latestLoad.current = load;
  });

  useEffect(() => {
    const target = end.current;
    if (!armed || target === null) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) latestLoad.current();
      },
      { root: scrollingAncestor(target), rootMargin: AHEAD },
    );
    observer.observe(target);
    return () => {
      observer.disconnect();
    };
  }, [end, armed]);
}
